<template>
    <f7-page ref="self" :page-content="false" class="settings-page" @page:beforein="pageBeforeInEvent">
        <f7-navbar>
            <f7-nav-left>
                <a href="#" class="link icon-only" @click="CLOSE">
                    <i class="icon-back icon"></i>
                </a>
            </f7-nav-left>
            <f7-nav-title>
                {{ $t('g.settings.page.title') }}
            </f7-nav-title>
            <f7-nav-right></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
            <p class="list-title">{{ $t('settings.my-profile') }}</p>
            <f7-list class="list-outline list-strong list-dividers">
                <f7-list-item :title="$t('g.settings.page.links.phone')"
                              :after="account.contact.phone"></f7-list-item>
                <f7-list-item :title="$t('g.settings.page.links.name')" link="/settings/change-short-name">
                    <template #after>
                        <template v-if="account.shortName && account.shortName.length > 0">
                            {{ account.shortName }}
                        </template>
                        <template v-else>
                            <i class="icon i-svg-warning"></i>
                        </template>
                    </template>
                </f7-list-item>
                <f7-list-item :title="$t('settings.user-id')" :after="account.id"/>
                <f7-list-item :title="$t('g.settings.page.links.language')" link="/settings/lang"
                              :after="detectCountryName(selectedLang)"></f7-list-item>
                <f7-list-item :title="$t('settings.remove-account')" link="/remove-account"></f7-list-item>
            </f7-list>
		        <f7-list
			        class="list-outline list-strong list-dividers"
			        v-if="isFaqButtonVisible || ViewRulesController.of().url.value"
		        >
				        <f7-list-item
						        v-if="isFaqButtonVisible"
						        @click="toFAQ"
						        class="settings-media-list-item"
						        :title="$t('faq.buttons.settings')"
						        link="#"
				        >
						        <template #before-title>
							          <i class="icon i-filled-question"></i>
						        </template>
				        </f7-list-item>
				        <f7-list-item
						        v-if="ViewRulesController.of().url.value"
						        @click="toRules"
						        class="settings-media-list-item"
						        :title="$t('g.settings.page.rules')"
						        link="#"
				        >
						        <template #before-title>
							          <i class="icon i-rules"></i>
						        </template>
				        </f7-list-item>
		        </f7-list>
            <version
                ref="versionRef"
                :releazioConfig="serviceRealizio?.activeConfig.value"
                :currentVersion="currentVersion"
                :currentBuild="currentBuild"
                :isDevEnvironment="isDevEnvironment"
                :isNeedUpdate="serviceRealizio?.isNeedUpdate.value"
                :isViewLastPost="serviceRealizio?.isViewedLastPost.value || !serviceRealizio.hasVersionPost()"
                :isCanShowUpdateButton="getDevice().cordova"
                @openDeeplink="openReleazioDeeplink"
                @openPosts="toReleazioPostsPage"
            />
        </f7-page-content>
    </f7-page>
</template>

<script lang="ts" setup>
import {f7} from 'framework7-vue';
import {Ref, ref} from "@vue/runtime-core";
// @ts-ignore
import AppController from "@target/components/App/ts/AppController";
import Version from "@components/atomic/version/Version.vue";
import {getDevice} from "framework7/shared/get-device";
import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import ModelAccount from "@models/v2/account/ModelAccount";
import RouterService from "@/services/RouterService";
import ServiceReleazio from "@/services/v2/service-releazio/ServiceReleazio";
import {computed, onMounted} from "vue";
import {PageOpenIn} from "@enums/PageOpenIn";
import ViewRulesController from "@/views/v2/rules/ViewRulesController";
import {useTranslate} from "@tolgee/vue";
import {ServiceFAQButtons} from "@/services/v2/service-faq-buttons/ServiceFAQButtons";

const {t} = useTranslate();
const account = ServiceAccount.of().account as Ref<ModelAccount>;
const faqButton = ServiceFAQButtons.of().settingsViewButton;
const version = import.meta.env.VITE_VERSION;
const serviceRouter = AppController.of().service<RouterService>("router");
const serviceRealizio: ServiceReleazio = AppController.of().service<ServiceReleazio>("releazio");
const currentVersion = import.meta.env.VITE_VERSION ?? "1.0.0";
const currentBuild = import.meta.env.VITE_BUILD ?? "-1";
const isDevEnvironment = import.meta.env.VITE_DEV === 'true';
const versionRef = ref(null);
let versionTimerRef: any = null;
const isFaqButtonVisible = computed(() => faqButton.value?.enabled && faqButton.value.link?.length);

const selectedLang = computed(() => {
	const localization = account.value.settings.localization;
	return localization === 'kk' ? 'kz' : localization;
});

onMounted(() => {
    initVersionEvents();
});

function toRules() {
    AppController.getInstance().routerService.toRules(PageOpenIn.PAGE, false);
}

function toFAQ() {
	AppController.getInstance().routerService.toFAQ(PageOpenIn.PAGE, false);
}

function pageBeforeInEvent() {
    if (serviceRealizio) {
        serviceRealizio.updateViewPost();
    }
}

function toReleazioPostsPage() {
    if (serviceRouter) {
        serviceRouter.toReleazioPostsPage();
    }
}

function openReleazioDeeplink() {
    if (serviceRealizio) {
        serviceRealizio.openDeepLink();
    }
}

function initVersionEvents() {
    if (versionRef.value && serviceRealizio) {
        const serviceRouter = AppController.of().service<RouterService>("router");
        serviceRealizio.initVersionEvents((versionRef.value as any).self, versionTimerRef, serviceRouter);
    }
}

const CLOSE = () => {
    f7.view.main.router.back('/payments', {force: true, clearPreviousHistory: true});
}

const detectCountryName = (systemName: string) => {
    switch (systemName) {
        case 'ru':
            return t.value("g.settings.selectLanguage.languages.ru");
        case 'en':
            return t.value("g.settings.selectLanguage.languages.en");
	    case 'kz':
		    return t.value("g.settings.selectLanguage.languages.kz");
	    case 'uz':
		    return t.value("g.settings.selectLanguage.languages.uz");
    }
    return systemName;
}
</script>

<style lang="scss" src="@/scss/views/settings-view.scss"></style>
<style lang="scss">
.settings-page__navbar {
    --f7-navbar-bg-color: #FFF;
}

.settings-media-list-item {
	.item-inner {
		gap: 8px;
		justify-content: start;
	}
}
</style>
